import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Gauge, Timer, Pencil, Check, Loader2, Activity, HeartPulse } from "lucide-react";
import { toast } from "sonner";

export function VmaCard({ onChange }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(null); // 'edit' | 'test'
  const [vmaInput, setVmaInput] = useState("");
  const [distInput, setDistInput] = useState("");
  const [estKm, setEstKm] = useState("");
  const [estMin, setEstMin] = useState("");
  const [busy, setBusy] = useState(false);
  const [canRegen, setCanRegen] = useState(false);
  const [regenning, setRegenning] = useState(false);

  const load = () => api.get("/profile/vma").then((r) => { setData(r.data); setVmaInput(r.data.vma || ""); });
  useEffect(() => { load(); }, []);

  const saveVma = async () => {
    const v = parseFloat(vmaInput);
    if (!v || v <= 0) { toast.error("VMA invalide"); return; }
    setBusy(true);
    try { const r = await api.post("/profile/vma", { vma: v }); setData(r.data); setMode(null); if (r.data.can_regenerate_precise) setCanRegen(true); toast.success("VMA mise à jour · allures recalculées"); onChange && onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const runTest = async () => {
    const dm = parseFloat(distInput);
    if (!dm || dm <= 0) { toast.error("Distance invalide"); return; }
    setBusy(true);
    try { const r = await api.post("/profile/vma-test", { distance_m: dm }); setData(r.data); setMode(null); setDistInput(""); if (r.data.can_regenerate_precise) setCanRegen(true); toast.success(`VMA calculée : ${r.data.vma} km/h · allures recalculées`); onChange && onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const runEstimate = async () => {
    const km = parseFloat(estKm), mn = parseFloat(estMin);
    if (!km || km <= 0 || !mn || mn <= 0) { toast.error("Distance et temps requis"); return; }
    setBusy(true);
    try { const r = await api.post("/profile/vma-estimate", { distance_km: km, time_min: mn }); setData(r.data); setMode(null); setEstKm(""); setEstMin(""); if (r.data.can_regenerate_precise) setCanRegen(true); toast.success(`VMA estimée : ${r.data.vma} km/h`); onChange && onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const regenerate = async () => {
    setRegenning(true);
    try { await api.post("/program/regenerate-vma"); setCanRegen(false); toast.success("Cycle régénéré en allures précises 🎯"); onChange && onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setRegenning(false); }
  };

  if (!data) return null;
  const beginner = (data.niveau || "").toLowerCase().includes("débutant") || (data.niveau || "").toLowerCase().includes("debutant");

  return (
    <div data-testid="vma-card" className="rounded-xl border border-border bg-white p-6">
      <div className="flex items-center justify-between">
        <div className="text-xs tracking-[0.16em] uppercase font-semibold text-obsidian/50 flex items-center gap-2"><Gauge size={15} /> VMA & allures</div>
        {data.vma && mode === null && (
          <div className="flex gap-1.5">
            <button data-testid="vma-edit-btn" onClick={() => setMode("edit")} className="text-xs inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 hover:bg-obsidian/5"><Pencil size={12} /> Modifier</button>
            <button data-testid="vma-test-btn" onClick={() => setMode("test")} className="text-xs inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 hover:bg-obsidian/5"><Timer size={12} /> Refaire le test</button>
            <button data-testid="vma-estimate-btn" onClick={() => setMode("estimate")} className="text-xs inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 hover:bg-obsidian/5"><Activity size={12} /> Estimer depuis une perf</button>
          </div>
        )}
      </div>

      {canRegen && (
        <div data-testid="regen-precise-banner" className="mt-4 rounded-xl bg-gradient-to-br from-sage/15 to-transparent border border-sage/30 p-4">
          <div className="flex items-start gap-3">
            <span className="shrink-0 w-9 h-9 rounded-full bg-sage/20 flex items-center justify-center"><Activity size={17} className="text-sage" /></span>
            <div className="flex-1">
              <div className="text-sm font-semibold">Ta VMA est enregistrée 🎯</div>
              <p className="mt-0.5 text-sm text-obsidian/65">Ton cycle actuel est en marche/course. Régénère-le en <span className="font-medium">allures précises</span> calculées sur ta VMA — ton historique et tes séances déjà faites sont conservés.</p>
              <button data-testid="regen-precise-button" onClick={regenerate} disabled={regenning}
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-obsidian text-white px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60">
                {regenning ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Régénérer en allures précises
              </button>
            </div>
          </div>
        </div>
      )}

      {data.vma ? (
        <div className="mt-3 flex items-baseline gap-2">
          <span data-testid="vma-value" className="font-display text-4xl font-light">{data.vma}</span>
          <span className="text-obsidian/50 text-sm">km/h</span>
          {data.source && <span className="ml-2 text-[11px] text-obsidian/40">source : {data.source}</span>}
          {data.source === "estimée" && <span className="ml-1 text-[10px] tracking-wide uppercase font-semibold rounded-full bg-amber-500/15 text-amber-600 border border-amber-500/30 px-2 py-0.5">estimation</span>}
        </div>
      ) : (
        <div data-testid="vma-empty" className="mt-3 rounded-lg bg-sage/8 border border-sage/25 p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-obsidian/80"><Activity size={16} className="text-sage" /> Ta VMA n'est pas encore définie</div>
          <p className="mt-1.5 text-sm text-obsidian/60">
            {beginner
              ? "En tant que débutant, indique tes allures habituelles à ton assistant, ou lance le test demi-Cooper quand tu te sens prêt : cours 6 minutes à ton maximum et saisis la distance parcourue."
              : "Lance le test demi-Cooper : cours 6 minutes à fréquence cardiaque maximale, puis saisis la distance totale parcourue (en mètres). On calcule ta VMA et toutes tes allures."}
          </p>
          <button data-testid="vma-start-test" onClick={() => setMode("test")} className="mt-3 rounded-full bg-obsidian text-white px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform"><Timer size={14} /> Lancer le test demi-Cooper</button>
          {!beginner && <button data-testid="vma-start-estimate" onClick={() => setMode("estimate")} className="mt-3 ml-2 rounded-full border border-obsidian px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 hover:bg-obsidian hover:text-white transition-colors"><Activity size={14} /> Estimer depuis une perf</button>}
        </div>
      )}

      {mode === "edit" && (
        <div className="mt-4 flex items-end gap-2">
          <div>
            <label className="block text-xs text-obsidian/50 mb-1">VMA (km/h)</label>
            <input data-testid="vma-input" type="number" step="0.1" value={vmaInput} onChange={(e) => setVmaInput(e.target.value)} className="w-32 border border-border rounded-sm px-3 py-2 text-sm focus:ring-1 focus:ring-obsidian focus:outline-none" />
          </div>
          <button data-testid="vma-save" onClick={saveVma} disabled={busy} className="rounded-sm bg-obsidian text-white px-4 py-2 text-sm inline-flex items-center gap-1.5">{busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Enregistrer</button>
          <button onClick={() => setMode(null)} className="rounded-sm border border-border px-4 py-2 text-sm">Annuler</button>
        </div>
      )}

      {mode === "test" && (
        <div className="mt-4 rounded-lg bg-secondary p-4">
          <div className="text-sm font-medium">Test demi-Cooper</div>
          <p className="mt-1 text-xs text-obsidian/60">Cours 6 minutes à fréquence cardiaque maximale, puis saisis la distance totale parcourue en mètres. VMA = distance / 100.</p>
          <div className="mt-3 flex items-end gap-2">
            <div>
              <label className="block text-xs text-obsidian/50 mb-1">Distance (m) sur 6 min</label>
              <input data-testid="vma-distance-input" type="number" placeholder="ex : 1500" value={distInput} onChange={(e) => setDistInput(e.target.value)} className="w-40 border border-border rounded-sm px-3 py-2 text-sm focus:ring-1 focus:ring-obsidian focus:outline-none" />
            </div>
            <button data-testid="vma-compute" onClick={runTest} disabled={busy} className="rounded-sm bg-obsidian text-white px-4 py-2 text-sm inline-flex items-center gap-1.5">{busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Calculer ma VMA</button>
            <button onClick={() => setMode(null)} className="rounded-sm border border-border px-4 py-2 text-sm">Annuler</button>
          </div>
        </div>
      )}

      {mode === "estimate" && (
        <div className="mt-4 rounded-lg bg-secondary p-4">
          <div className="text-sm font-medium">Estimer ma VMA depuis une performance</div>
          <p className="mt-1 text-xs text-obsidian/60">Saisis une course récente réalisée à ton maximum (ex : 10 km en 48 min). La VMA obtenue est une <span className="font-medium">estimation</span>, affinable ensuite par un test.</p>
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs text-obsidian/50 mb-1">Distance (km)</label>
              <input data-testid="vma-est-km" type="number" step="0.1" placeholder="ex : 10" value={estKm} onChange={(e) => setEstKm(e.target.value)} className="w-28 border border-border rounded-sm px-3 py-2 text-sm focus:ring-1 focus:ring-obsidian focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-obsidian/50 mb-1">Temps (min)</label>
              <input data-testid="vma-est-min" type="number" step="0.1" placeholder="ex : 48" value={estMin} onChange={(e) => setEstMin(e.target.value)} className="w-28 border border-border rounded-sm px-3 py-2 text-sm focus:ring-1 focus:ring-obsidian focus:outline-none" />
            </div>
            <button data-testid="vma-est-compute" onClick={runEstimate} disabled={busy} className="rounded-sm bg-obsidian text-white px-4 py-2 text-sm inline-flex items-center gap-1.5">{busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Estimer</button>
            <button onClick={() => setMode(null)} className="rounded-sm border border-border px-4 py-2 text-sm">Annuler</button>
          </div>
        </div>
      )}

      {data.vma && (
        <div className="mt-5">
          <div className="text-xs sm:text-sm text-obsidian/45 mb-2.5">Allures d'entraînement & FC cible calculées depuis ta VMA</div>
          <div data-testid="vma-paces" className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {data.paces.map((p) => (
              <div key={p.zone} className="rounded-xl border border-border px-3.5 py-2.5">
                <div className="text-[11px] sm:text-xs text-obsidian/50 truncate">{p.zone}</div>
                <div className="text-sm sm:text-base font-medium mt-0.5">{p.pace}</div>
                <div className="mt-1 flex items-center gap-1.5 text-[11px] text-obsidian/45">
                  <span className="tabnum">{p.pct}% VMA</span>
                  {p.fcmax ? <span className="inline-flex items-center gap-1 text-[#B4413C]"><HeartPulse size={11} /> ~{p.fcmax}% FCmax</span> : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
